import React, { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import { Modal, Form, Input, Button, Row, Col, Upload } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import axios from "axios";
import { hostname } from "../hooks/useEndeavour";
import { useMutation, useQuery } from "@tanstack/react-query";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { set } from "@ant-design/plots/es/core/utils";

const { Dragger } = Upload;

const UpdateCampaignSortModal = forwardRef((props, ref) => {
  const [visible, setVisible] = useState(false);
  const [files, setFiles] = useState([]);
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useImperativeHandle(ref, () => ({
    openModal: () => setVisible(true),
    closeModal: () => {
      setVisible(false);
      form.resetFields();
    },
  }));

  const query = useQuery({
    queryKey: ["update campaign sort"],
    queryFn: () => {
      return axios
        .get(`${hostname}/pa-alliance/campaign/event/sort`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        })
        .then((res) => {
          // Create a new array with the desired structure
          const data = res.data.payload.map((item) => ({
            id: String(item.id),
            content: item.name,
          }));

          setItems(data);
          return data;
        });
    },
  });
  const submit = useMutation({
    mutationFn: (data) => {
      return axios
        .post(`${hostname}/pa-alliance/campaign/event/sort`, data, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        })
        .then((res) => {
          setLoading(false);
          query.refetch();
        });
    },
  });

  useEffect(() => {
    if (visible == true) {
      query.refetch();
    }
  }, [visible]);

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64Data = reader.result.split(",")[1];
        resolve(base64Data);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const draggerConfig = {
    name: "file",
    multiple: false,
    fileList: files,
    showUploadList: true,
    beforeUpload: async (file) => {
      const base64 = await getBase64(file);
      const currentFiles = {
        uid: file.uid,
        name: file.name,
        type: file.type,
        size: file.size,
        base64: base64,
      };
      setFiles([currentFiles]);
    },
    onRemove: (file) => {
      handleRemove(file);
    },
  };

  const handleRemove = (file) => {
    setFiles((prevFiles) => prevFiles.filter((f) => f.uid !== file.uid));
  };

  const onSubmit = () => {
    setLoading(true);

    form.validateFields().then((values) => {
      let data = [];
      for (let i = items.length - 1; i >= 0; i--) {
        data.push({
          id: items[i].id,
          name: items[i].content,
        });
      }
      values.sort_order = data;
      submit.mutate(values);
    });
  };

  // Sample items for drag and drop

  // Function to reorder the result
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  // Style for the draggable items
  const getItemStyle = (isDragging, draggableStyle) => ({
    userSelect: "none",
    padding: 16,
    margin: "0 0 8px 0",
    background: isDragging ? "#f9f9f9" : "#ffffff",
    border: "1px solid #d9d9d9",
    borderRadius: "2px",
    ...draggableStyle,
  });

  // Style for the droppable container
  const getListStyle = (isDraggingOver) => ({
    background: isDraggingOver ? "#e6f7ff" : "#f0f2f5",
    padding: 8,
    width: "100%",
  });

  const onDragEnd = (result) => {
    // Dropped outside the list
    if (!result.destination) {
      return;
    }

    const newItems = reorder(items, result.source.index, result.destination.index);

    setItems(newItems);
  };

  return (
    <Modal
      title="Update Campaign Sorting"
      open={visible}
      onCancel={() => {
        setVisible(false);
      }}
      footer={null}
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={onSubmit}
        initialValues={{
          start_date: new Date().toISOString().split("T")[0],
          expired_date: new Date().toISOString().split("T")[0],
        }}
      >
        <Form.Item label="Sort Campaign" name="sort_order">
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable" type="DROPPABLE">
              {(provided, snapshot) => (
                <div {...provided.droppableProps} ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)}>
                  {items.map((item, index) => (
                    <Draggable key={item.id} draggableId={item.id} index={index} type="DROPPABLE">
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                        >
                          {item.content}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>
            Update
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
});

export default UpdateCampaignSortModal;
